import React, { memo, useEffect, useRef, useState } from "react";
import closeIcon from "@qlik/qlik/src/assets/icons/close.svg";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { handleKeyboardEventBehavior } from "@qlik/qlik/src/lib/util/helpers";
import { useAppContext } from "@qlik/qlik/src/context/AppContext";

const CustomerSliderCardPopup = memo(({ card, className = "", active = false, onClose, index }) => {
    // Refs for the wrapper elements
    const wrapperRefs = useRef([]);
    // State for the open popup index
    const [openPopupIndex, setOpenPopupIndex] = useState(null);
    const { setBodyClassName } = useAppContext();

    // Function to handle click outside of the popup
    const handleClickOutside = (event) => {
        wrapperRefs.current.forEach((wrapperRef, i) => {
            if (wrapperRef && !wrapperRef.current.contains(event.target)) {
                if (i === openPopupIndex) {
                    onClose();
                } else {
                    setOpenPopupIndex(null);
                }
            }
        });
    };

    // Function to handle escape key press
    const handleEscapeKeyPress = (event) => {
        if (event.key === "Escape") {
            onClose();
        }
    };

    // Add event listener for escape key press
    useEffect(() => {
        document.addEventListener("keydown", handleEscapeKeyPress);
        return () => {
            document.removeEventListener("keydown", handleEscapeKeyPress);
        };
    });

    // Initialize ref for the current index
    useEffect(() => {
        wrapperRefs.current[index] = wrapperRefs.current[index] || React.createRef();
    }, [index]);

    // Update open popup index when active or index changes
    useEffect(() => {
        if (active) {
            setOpenPopupIndex(index);
            setBodyClassName("overflow-hidden");
        } else {
            setOpenPopupIndex(null);
        }
    }, [active, index]);

    // Render the popup card
    return (
        <div
            role="button"
            onClick={handleClickOutside}
            tabIndex={-1}
            onKeyDown={(e) => {
                handleKeyboardEventBehavior(
                    e,
                    () => {
                        handleClickOutside;
                    },
                    ["Escape"]
                );
            }}
            className={`${
                active ? "" : "opacity-0 pointer-events-none"
            } fixed z-popup inset-0 text-white transition-all flex items-center	${className}`}
        >
            <div ref={wrapperRefs.current[index]} className="bg-blue p-7.5">
                <div className="flex justify-end items-center mb-5 gap-10">
                    {card.subtitle && (
                        <p className="text-white leading-none tracking-wide1 uppercase font-bold text-xs sm:text-base-1 sm:-tracking-wide3">
                            {card.subtitle}
                        </p>
                    )}
                    <button
                        aria-label="icon"
                        className="text-xs w-6.75 h-6.75 leading-none"
                        type="button"
                        onClick={onClose}
                    >
                        <span
                            style={{ backgroundImage: `url(${closeIcon})` }}
                            className="w-full h-full brightness-0 invert block bg-center bg-no-repeat"
                        />
                    </button>
                </div>
                {card.title && (
                    <p
                        className={`text-white text-xl font-semibold leading-tight tracking-tight3 mb-5 sm:tracking-normal ${
                            card.cta ? "line-clamp-4" : "line-clamp-none"
                        }`}
                    >
                        {card.title}
                    </p>
                )}
                {card?.description?.description && (
                    <p className="text-white mb-8 line-clamp-9">{card?.description?.description}</p>
                )}
                <div className="flex justify-between gap-10 items-end">
                    {card?.mediaIcon && (
                        <Media
                            media={card?.mediaIcon}
                            className="object-scale-down h-10 object-left-bottom lg:h-15 brightness-0 invert flex-shrink w-30"
                        />
                    )}
                    <div className="flex-grow flex justify-end flex-wrap leading-none gap-4 ml-auto">
                        {card?.cta?.map((button) => (
                            <Cta
                                key={button.id}
                                data={button}
                                className="text-sm font-bold text-white transition-colors"
                            >
                                <span>{button.text}</span>
                            </Cta>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default CustomerSliderCardPopup;
